import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import { useState } from "react";
import { CreatePlanForm } from "./create-plan.form";
import { useSaveData } from "../../../../../../../hooks/useSaveData";
import { ReactHookForm } from "../../../../../../../components/hook-form/react-hook-form.component";
import { MutationKeyConfig } from "../../../../../../../api/api-helper";
import { InvalidateRoute } from "../../../../../../../hooks/useDeleteData";
import { useReactHookForm } from "../../../../../../../hooks/useReactHookForm";
import { parseErrorMessageRecursive } from "../../../../../../../utils/yup-error-parser.util";
import { useSnackbar } from "../../../../../../../hooks/use-snackbar.hook";
import { useAuthorize } from "../../../../../../../hooks/use-authorize";
import { UserPermissionName } from "../../../../../../../models/user-permission-name";

interface PlanRequestArgs {
    customerID?: number;
    projectID?: number;
    officeID?: number;
    unitID?: number;
}

interface CreatePlanDialog {
    data: any;
    yupSchema?: any;
    planListData: any[];
    planRequestArgs: PlanRequestArgs;
    onSavedPlan: () => void;
    onClosed: () => void
    config: { saveConfig: MutationKeyConfig; invalidateRoute?: InvalidateRoute };
}
export const CreatePlanComponent = ({
    data,
    yupSchema,
    planListData,
    planRequestArgs,
    onSavedPlan,
    onClosed,
    config: { saveConfig, invalidateRoute },
}: CreatePlanDialog) => {

    const form = useReactHookForm({
        defaultValues: {
            ...data,
            customer: Number(data?.customerID) ? { customerID: Number(data?.customerID), customerName: '' } : null,
            office: Number(data?.officeID) ? { officeID: Number(data?.officeID), officeName: '' } : null,
            unitID: data?.unitID,
            sqFootage: data?.area ?? '',
            volume: data?.volume ?? '',
            worstcaseScore: data?.worstcaseScore ?? '',
            windowArea: data?.windowArea ?? '',
            associatedPlan: { planID: Number(data?.planAssociatedWithPlanID), planName: '' },
            wallHeight: data?.wallHeight ?? '',
            uploadedFile: data?.fileName ?? '',
            uploadedFileType: data?.fileType ?? '',
        },
        yupSchema,
    });
    const { showSnackbar } = useSnackbar();
    const [planReqArgs, setPlanReqArgs] = useState(planRequestArgs);
    const [fileData, setFileData] = useState<File>();
    const [isFileDeleted, setFileDeleted] = useState<boolean>(false);

    const { isLoading, isError, error, mutateAsync } = useSaveData(saveConfig, invalidateRoute);

    const getExtension = (fileName: string) => {
        const extension = "." + fileName?.split('.').pop();
        return extension;
    }

    const validateFileExtension = (extension: string) => {
        if (extension == '.enb') {
            const checkFileExtension = planListData?.some((plan: any) => plan.fileType === extension);
            if (checkFileExtension) {
                showSnackbar('Plan already has an ENB file.', 'error');
                return true
            }
        }
        return false;
    }

    const validatePlanType = (planFileType: number) => {
        if (planFileType == 1 || planFileType == 3) {
            const checkPlanType = planListData?.some((plan: any) => plan.planFileTypeID === Number(planFileType));
            if (checkPlanType) {
                showSnackbar(`Plan already created for ${planFileType == 1 ? 'master plan' : 'energy modeling file'}`, 'error');
                return true
            }
        }
        return false;
    }

    const handleSubmit = async (value: any) => {
        if (value?.planID) {
            if (data?.planFileTypeID !== 1 && data?.planFileTypeID !== 3) {
                if (validatePlanType(value?.planFileTypeID)) return
            }
        }
        else {
            if (validatePlanType(value?.planFileTypeID)) return;
        }

        const extension = fileData ? getExtension(fileData?.name) : value?.uploadedFile ? getExtension(value?.uploadedFile) : "";
        if (value?.planID) {
            if (data?.fileType !== '.enb') {
                if (validateFileExtension(extension)) return
            }
        }
        else {
            if (validateFileExtension(extension)) return;
        }
        const formData = new FormData();
        formData.append('PlanID', value?.planID ?? '');
        formData.append('PlanName', value?.planName ?? '');
        formData.append('CustomerID', value?.customer?.customerID ?? '');
        // formData.append('OfficeID', value?.office?.officeID ?? '');
        formData.append('UnitID', value?.unitID ?? '');
        formData.append('UploadedFile', fileData ?? '');
        formData.append('PlanFileName', fileData ? fileData?.name : value?.uploadedFile ?? '');
        formData.append('PlanFileType', extension);
        formData.append('PlanFileTypeID', value?.planFileTypeID ?? '');
        formData.append('Area', value?.sqFootage ?? '');
        formData.append('Volume', value?.volume ?? '');
        formData.append('WorstcaseScore', value?.worstcaseScore ?? '');
        formData.append('WindowArea', value?.windowArea ?? '');
        formData.append('WallHeight', value?.wallHeight ?? '');
        formData.append('AssociatedPlanID', value?.associatedPlan?.planID);
        if (isFileDeleted) formData.append('IsFileDeleted', isFileDeleted.toString());

        await mutateAsync({ data: formData });
        onSavedPlan();
    }

    const handleUploadFile = (file: File) => {
        setFileData(file);
        setFileDeleted(false);
        if (data?.fileName && !file) setFileDeleted(true)
    }

    const handleError = (error: any) => {
        const messages = parseErrorMessageRecursive(error);

        let requiredMessages: any[] = [];
        let otherMessages: any[] = [];

        // Classify messages
        Array.isArray(messages) &&
            messages?.forEach((message) => {
                if (message.endsWith('is required')) {
                    requiredMessages.push(message?.split(' is required')[0]?.trim());
                } else {
                    otherMessages.push(message.trim());
                }
            });

        // Remove duplicates
        requiredMessages = Array.from(new Set(requiredMessages));
        otherMessages = Array.from(new Set(otherMessages));

        // Construct the final message
        const finalRequiredMessage = requiredMessages.length > 0 ? `${requiredMessages?.join(', ')} is required` : '';
        const finalOtherMessage = otherMessages?.join(', ');

        // Combine messages
        const finalMessage = [finalRequiredMessage, finalOtherMessage]?.filter(Boolean)?.join(', ');

        // Show the final formatted message
        showSnackbar(`Validation Error: ${finalMessage}`, 'error');
    };

    const handleFormSubmit = form.handleSubmit(handleSubmit as any, handleError);

    const isReadOnlyPermission = useAuthorize([UserPermissionName?.READ_ONLY]);

    return (
        <ReactHookForm formName="UPLOAD_PLAN_FORM" onSubmit={() => { }} formContextProps={form} isBusy={isLoading}>
            <Grid container spacing={2}>
                <Grid item lg={12} sm={12} xl={12} xs={12}>
                    <CreatePlanForm isEdit={!!data?.planID} uploadFile={handleUploadFile} planRequestArgs={planReqArgs} />
                </Grid>
                <Grid item lg={12} sm={12} xl={12} xs={12} className="spaceBetween">
                    <Button variant="outlined" style={{ width: '130px' }} onClick={onClosed}>
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        variant="contained"
                        style={{ width: '130px' }}
                        onClick={handleFormSubmit}
                        disabled={isReadOnlyPermission}
                    >
                        Save
                    </Button>
                </Grid>
            </Grid>
        </ReactHookForm>
    );
}